/**
 * EntranceExitLogs Repository
 * 입출입 로그 데이터 API 호출 계층
 */
import { API_BASE_URL } from '../../../config/api';
import type {
  EntranceExitStats,
  LogsResponse,
  ExportResponse,
  LogFilter,
  EntranceExitLog
} from '../models/EntranceExitLogs';
import { buildLogQueryParams } from '../models/EntranceExitLogs';

interface LogicalTodayResponse {
  success: boolean;
  data: {
    logical_today: string;
    physical_today: string;
    current_time: string;
    day_boundary_hour: number;
  };
}

export class EntranceExitLogsRepository {
  /**
   * 조직 ID 가져오기 (AuthStore 우선, localStorage 폴백)
   */
  private getOrganizationId(): string | null {
    const authStorage = localStorage.getItem('auth-storage');
    let organizationId: string | null = null;

    if (authStorage) {
      try {
        const parsed = JSON.parse(authStorage);
        organizationId = parsed.state?.user?.organization_id;
      } catch (e) {
        console.warn('AuthStore 파싱 실패:', e);
      }
    }

    if (!organizationId) {
      organizationId = localStorage.getItem('organization_id');
    }

    return organizationId;
  }

  /**
   * 인증 토큰 가져오기
   */
  private getAuthToken(): string | null {
    return localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
  }

  /**
   * 입출입 통계 조회
   */
  async getStats(date: string): Promise<EntranceExitStats | null> {
    try {
      const token = this.getAuthToken();

      const response = await fetch(
        `${API_BASE_URL}/v1/owner/entrance-exit/stats?date=${date}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('통계 조회 실패');
      }

      return await response.json();
    } catch (error) {
      console.error('통계 로드 실패:', error);
      return null;
    }
  }

  /**
   * 입출입 로그 조회 (페이지네이션)
   *
   * TODO: 백엔드에 pagination 지원하는 엔드포인트 추가되면 변경 필요
   * - GET /v1/access-logs/paginated?page=1&page_size=20&start_date=...
   *
   * 현재는 프론트엔드에서 페이지네이션 처리
   */
  async getLogs(params: {
    page: number;
    pageSize: number;
    filter: LogFilter;
  }): Promise<LogsResponse | null> {
    try {
      const organizationId = this.getOrganizationId();

      // API 파라미터 구성
      // - 날짜 필터 없으면: 최근 30건
      // - 날짜 필터 있으면: 해당 날짜 범위에서 최대 1000건
      const hasDateFilter = params.filter.startDate && params.filter.endDate;
      const urlParams = new URLSearchParams({
        limit: hasDateFilter ? '1000' : '30',
      });

      if (organizationId) {
        urlParams.append('org_id', organizationId);
      }

      // 날짜 필터 적용
      if (hasDateFilter) {
        urlParams.append('start_date', params.filter.startDate!);
        urlParams.append('end_date', params.filter.endDate!);
      }

      // 이름 검색 및 액션 필터 적용
      if (params.filter.userName) {
        urlParams.append('user_name', params.filter.userName);
      }
      if (params.filter.action && params.filter.action !== 'all') {
        urlParams.append('action', params.filter.action);
      }

      const url = `${API_BASE_URL}/v1/owner/entrance-exit/logs?${urlParams}`;  // Supabase DB 최적화 엔드포인트

      const token = this.getAuthToken();
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });


      if (!response.ok) {
        const errorText = await response.text();
        console.error('[API] 응답 실패:', errorText);
        throw new Error('로그 조회 실패');
      }

      const result = await response.json();

      // 백엔드 응답 구조: { success: true, data: { logs: [], total_count: 0 } }
      const rawLogs: EntranceExitLog[] = result.data?.logs || [];

      // TODO: 백엔드에서 필터링/페이지네이션 지원 시 아래 로직 제거
      // 현재는 프론트엔드에서 필터링
      let filteredLogs = this.filterLogs(rawLogs, params.filter);

      // 페이지네이션 계산
      const total = filteredLogs.length;
      const totalPages = Math.ceil(total / params.pageSize);
      const offset = (params.page - 1) * params.pageSize;
      const paginatedLogs = filteredLogs.slice(offset, offset + params.pageSize);

      return {
        success: true,
        data: {
          items: paginatedLogs,
          pagination: {
            page: params.page,
            page_size: params.pageSize,
            total: total,
            total_pages: totalPages,
            has_next: params.page < totalPages,
            has_prev: params.page > 1,
          },
        },
      };
    } catch (error) {
      console.error('로그 로드 실패:', error);
      return null;
    }
  }

  /**
   * Excel Export용 로그 조회
   *
   * TODO: 백엔드에 export 전용 엔드포인트 추가되면 변경 필요
   * - GET /v1/access-logs/export?start_date=...&max_limit=5000
   * - 응답: { success, data: { logs, total, exported, is_truncated, max_limit } }
   */
  async exportLogs(filter: LogFilter): Promise<ExportResponse | null> {
    try {
      const organizationId = this.getOrganizationId();

      // 현재는 일반 조회 API 사용 (limit 크게)
      const urlParams = new URLSearchParams({
        limit: '5000', // Export용 최대 제한
      });

      if (organizationId) {
        urlParams.append('org_id', organizationId);
      }

      const filterParams = buildLogQueryParams(filter);
      filterParams.forEach((value, key) => {
        urlParams.append(key, value);
      });

      const url = `${API_BASE_URL}/v1/owner/entrance-exit/logs?${urlParams}`;  // Supabase DB 최적화 엔드포인트

      const token = this.getAuthToken();
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error('Export 로그 조회 실패');
      }

      const result = await response.json();
      const rawLogs: EntranceExitLog[] = result.data?.logs || [];

      // 프론트엔드에서 필터링
      const filteredLogs = this.filterLogs(rawLogs, filter);

      // Export 제한 체크 (5000건)
      const MAX_EXPORT_LIMIT = 5000;
      const total = filteredLogs.length;
      const exported = Math.min(total, MAX_EXPORT_LIMIT);
      const exportedLogs = filteredLogs.slice(0, MAX_EXPORT_LIMIT);
      const isTruncated = total > MAX_EXPORT_LIMIT;

      return {
        success: true,
        data: {
          logs: exportedLogs,
          total: total,
          exported: exported,
          is_truncated: isTruncated,
          max_limit: MAX_EXPORT_LIMIT,
        },
      };
    } catch (error) {
      console.error('Export 로그 로드 실패:', error);
      return null;
    }
  }

  /**
   * 프론트엔드 필터링 로직
   * TODO: 백엔드에서 필터링 지원 시 제거
   */
  private filterLogs(logs: EntranceExitLog[], filter: LogFilter): EntranceExitLog[] {
    let filtered = [...logs];

    // 날짜 필터링
    if (filter.startDate && filter.endDate) {
      const [startYear, startMonth, startDay] = filter.startDate.split('-').map(Number);
      const [endYear, endMonth, endDay] = filter.endDate.split('-').map(Number);
      const startTime = new Date(startYear, startMonth - 1, startDay, 0, 0, 0);
      const endTime = new Date(endYear, endMonth - 1, endDay, 23, 59, 59);

      filtered = filtered.filter(log => {
        const logDate = new Date(log.timestamp);
        return logDate >= startTime && logDate <= endTime;
      });
    }

    // 이름 필터링
    if (filter.userName) {
      filtered = filtered.filter(log =>
        log.user_name?.includes(filter.userName!)
      );
    }

    // 액션 필터링
    if (filter.action && filter.action !== 'all') {
      filtered = filtered.filter(log => log.action === filter.action);
    }

    // 최신순 정렬
    filtered.sort((a, b) =>
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );

    return filtered;
  }

  /**
   * 순공부시간 기준에 따른 논리적 '오늘' 날짜 조회
   *
   * 예: day_boundary_hour=4 설정일 때
   * - 12월 15일 03:00 → 12월 14일 반환 (새벽 4시 이전은 전날로 계산)
   * - 12월 15일 04:00 → 12월 15일 반환
   */
  async getLogicalToday(): Promise<LogicalTodayResponse | null> {
    try {
      const token = this.getAuthToken();
      const url = `${API_BASE_URL}/v1/owner/entrance-exit/logical-today`;


      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        console.error('[API] 논리적 오늘 조회 실패:', response.status);
        return null;
      }

      const result: LogicalTodayResponse = await response.json();

      return result;
    } catch (error) {
      console.error('논리적 오늘 조회 실패:', error);
      return null;
    }
  }
}

export const entranceExitLogsRepository = new EntranceExitLogsRepository();
